import { useCallback, useEffect, useMemo, useState } from 'react';
import './styles.css';
import { useDispatch, useSelector } from 'react-redux';
import { UIRootState } from 'ui/store';
import { translate } from 'game/translate';
import { getStringWithParams } from 'game/quests/utils';
import { toggleIsNewMission } from 'ui/store/actions/gameUIActions';
import { useDomAwareVisibility } from 'ui/hooks/useOnScreen';
import { GAEvent, GAEventNames } from 'game/analytics/events';
import { SoundManager } from 'game/core/soundManager';

interface MissionsButtonProps {
    type: 'main' | 'secondary';
    onClick: () => void;
}

export const MissionsButton: React.FC<MissionsButtonProps> = ({ onClick, type }) => {
    const dispatch = useDispatch();
    const isNewMissionCompleted = useSelector((state: UIRootState) => state.gameUI.missions?.isNewMissionCompleted);
    const isNewMissionCompletedOnMounted = useSelector((state: UIRootState) => state.gameUI.missions?.isNewMissionCompletedOnMounted);
    const dailyMission = useSelector((state: UIRootState) => state.gameUI.missions?.dailyMission);
    const isAllMissionsCompleted = useMemo(() => !dailyMission?.rewardsClaimed && !dailyMission?.actions.find(x => !x.passed), [dailyMission]);

    const dailyMissionCompletedCount = useMemo(() => dailyMission?.actions.filter(x => x.passed).length || 0, [dailyMission]);
    const dailyMissionTotalCount = useMemo(() => dailyMission?.actions.length || 0, [dailyMission]);

    const isMissionsVisited = localStorage.getItem('missions-visited') == 'true';

    const [isTooltipVisible, setIsTooltipVisible] = useState(false);
    const [isForceCheckedIcon, setIsForceCheckedIcon] = useState(false);
    const [tooltipText, setTooltipText] = useState("");

    const { ref, isVisible } = useDomAwareVisibility(700);

    const handleMissionsClick = useCallback(() => {
        localStorage.setItem('missions-visited', 'true');
        dispatch(toggleIsNewMission(false));
        setIsForceCheckedIcon(false);
        GAEvent(GAEventNames.MISSION_BUTTON_CLICK);
        onClick();
    }, [onClick, dispatch, toggleIsNewMission]);

    const missionsIcon = useMemo(() => {
        const chestImageName = dailyMission?.isAdditionalMission ? 'reward_box_small_2_secondary' : 'reward_box_small_secondary';

        const chestImage = (
            <>
                <picture>
                    <source srcSet={`/assets/missions/small-particles.webp`} type="image/webp" />
                    <img style={{ height: '25px', zIndex: 0, position: 'absolute', top: '10px', left: '8px' }} src={`/assets/missions/small-particles.png`}/>
                </picture>
                <picture>
                    <source srcSet={`/assets/missions/${chestImageName}.webp`} type="image/webp" />
                    <img style={{ height: '34px', zIndex: 1, position: 'relative', top: '2px', left: '1px' }} src={`/assets/missions/${chestImageName}.png`}/>
                </picture>
            </>
            
        );
        
        if (isAllMissionsCompleted) {
            return chestImage;
        }

        if ((isNewMissionCompleted || isForceCheckedIcon) && !isAllMissionsCompleted) {
            return (
                <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" fill="none">
                    <path d="M26.2537 6.14451C25.811 5.69718 25.0884 5.69717 24.6457 6.1445L14.2438 16.6561C13.8011 17.1034 13.0784 17.1034 12.6358 16.6561L7.80432 11.7735C7.36166 11.3262 6.63898 11.3262 6.19632 11.7735L3.45329 14.5455C3.01716 14.9862 3.01716 15.6959 3.45328 16.1367L9.10544 21.8486L12.6641 25.4448C13.1067 25.8922 13.8294 25.8922 14.272 25.4448L17.8024 21.8772L29.025 10.5363C29.4612 10.0956 29.4612 9.38588 29.025 8.94514L26.2537 6.14451Z" fill="white"/>
                </svg>
            );
        }

        if (!isNewMissionCompleted && !isAllMissionsCompleted) {
            return (
                <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" fill="none">
                    <path d="M23.6152 4.73438C25.4845 4.73438 27 6.28658 27 8.20117V25.5342C27 27.4488 25.4845 29.001 23.6152 29.001H8.38477C6.51551 29.001 5 27.4488 5 25.5342V8.20117C5 6.28658 6.51551 4.73438 8.38477 4.73438C8.38483 6.6489 9.90031 8.20117 11.7695 8.20117H20.2305C22.0997 8.20117 23.6152 6.6489 23.6152 4.73438ZM9.6543 22.0674C8.95332 22.0674 8.38477 22.6502 8.38477 23.3682C8.38494 24.086 8.95343 24.668 9.6543 24.668C10.3551 24.6678 10.9227 24.0859 10.9229 23.3682C10.9229 22.6503 10.3552 22.0675 9.6543 22.0674ZM13.8848 22.0674C13.1838 22.0674 12.6152 22.6502 12.6152 23.3682C12.6154 24.086 13.1839 24.6679 13.8848 24.668H22.3467C23.0474 24.6678 23.6151 24.0859 23.6152 23.3682C23.6152 22.6503 23.0475 22.0676 22.3467 22.0674H13.8848ZM9.6543 16.8682C8.95332 16.8682 8.38477 17.45 8.38477 18.168C8.38483 18.8859 8.95337 19.4678 9.6543 19.4678C10.3551 19.4677 10.9228 18.8858 10.9229 18.168C10.9229 17.4501 10.3552 16.8683 9.6543 16.8682ZM13.8848 16.8682C13.1838 16.8682 12.6152 17.45 12.6152 18.168C12.6153 18.8859 13.1839 19.4677 13.8848 19.4678H22.3467C23.0474 19.4676 23.6152 18.8858 23.6152 18.168C23.6152 17.4501 23.0475 16.8684 22.3467 16.8682H13.8848ZM9.6543 11.668C8.95335 11.668 8.3848 12.2498 8.38477 12.9678C8.38477 13.6857 8.95332 14.2676 9.6543 14.2676C10.3552 14.2675 10.9229 13.6857 10.9229 12.9678C10.9228 12.2499 10.3551 11.6681 9.6543 11.668ZM13.8848 11.668C13.1838 11.668 12.6153 12.2499 12.6152 12.9678C12.6152 13.6857 13.1838 14.2675 13.8848 14.2676H22.3467C23.0475 14.2674 23.6152 13.6856 23.6152 12.9678C23.6152 12.25 23.0475 11.6682 22.3467 11.668H13.8848ZM20.1904 3C21.1477 3 21.9238 3.7761 21.9238 4.7334C21.9238 5.69066 21.1477 6.4668 20.1904 6.4668H11.8115C10.8543 6.4668 10.0782 5.69066 10.0781 4.7334C10.0781 3.7761 10.8542 3 11.8115 3H20.1904Z" fill="white"/>
                </svg>
            );
        }

        return chestImage;
    }, [isNewMissionCompleted, isAllMissionsCompleted, dailyMission?.isAdditionalMission, isForceCheckedIcon]);

    const handleShowTooltipData = useCallback((show: boolean) => {
        if (isAllMissionsCompleted && show) {
            setTooltipText(translate('mission_button_tooltip_text_completed'));
            return;
        }
        
        if(!isAllMissionsCompleted && show) {
            setTooltipText(getStringWithParams(translate('mission_button_tooltip_text_progress'), dailyMissionCompletedCount.toString(), dailyMissionTotalCount.toString()));
            return;
        }
    }, [isAllMissionsCompleted, dailyMissionCompletedCount, dailyMissionTotalCount]);

    const handleShowDelayedTooltip = useCallback(() => {
        setIsTooltipVisible(true);
        handleShowTooltipData(true);
        setIsForceCheckedIcon(true);

        setTimeout(() => {
            dispatch(toggleIsNewMission(false));
            setIsForceCheckedIcon(false);
        }, 10000);
    }, [handleShowTooltipData, dispatch]);

    useEffect(() => {
        if (isVisible && !!isNewMissionCompletedOnMounted) {
            handleShowDelayedTooltip();
        }
    }, [isVisible]);

    useEffect(() => {
        if (isNewMissionCompletedOnMounted) {

            if (isVisible) {
                handleShowDelayedTooltip();
            }

            return;
        }

        setIsTooltipVisible(!!isNewMissionCompleted);
        handleShowTooltipData(!!isNewMissionCompleted);

        setIsTooltipVisible(false);
    }, [isAllMissionsCompleted, isNewMissionCompleted, dailyMissionCompletedCount, dailyMissionTotalCount, isNewMissionCompletedOnMounted]);

    useEffect(() => {
        if (!isNewMissionCompleted) {
            setIsForceCheckedIcon(false);
        }
    }, [isNewMissionCompleted]);

    const handleTooltipClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
        e.stopPropagation();
        dispatch(toggleIsNewMission(false));
        setIsForceCheckedIcon(false);
    }, [dispatch, toggleIsNewMission]);

    useEffect(() => {
        if (isTooltipVisible) {
            SoundManager.play('missionCompletedSFX');
        }
    }, [isTooltipVisible]);

    if (!dailyMission) {
        return null;
    }

    return (
        <div ref={ref} className={`mission-button mission-button--${type}`}>
            <button 
                id="missionsButton" 
                onClick={handleMissionsClick}
                className="circle-btn btn btn-outline-light border-0 rounded-circle position-relative d-flex align-items-center justify-content-center p-0" 
                style={{ width: '55px', height: '55px' }}
            >
                {missionsIcon}
            </button>

            {!isMissionsVisited && <div className="mission-button-indicator"/>}

            <div onClick={handleTooltipClick} className={`mission-button-tooltip mission-button-tooltip--${type} ${isTooltipVisible ? 'mission-button-tooltip--visible' : 'mission-button-tooltip--invisible'}`}>
                {tooltipText}
                <svg className={`mission-button-tooltip-arrow--${type}`} xmlns="http://www.w3.org/2000/svg" width="7" height="12" viewBox="0 0 7 12" fill="none">
                    <path d="M0 0L5.86667 4.4C6.93333 5.2 6.93333 6.8 5.86667 7.6L0 12V0Z" fill="currentColor"/>
                </svg>
            </div>
        </div>
    );
};